import { useEffect, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useAuth, useCartCount } from "@/hooks/use-auth";
import { AUTH_ROUTES, NAV_LINKS } from "./nav-config";
import { ThemeToggle } from "./ThemeToggle";
import cb16Logo from "@/assets/CB16_Logo.svg.asset.json";

const NAV_LINK_CLASS =
  "text-[11px] font-medium uppercase tracking-[0.22em] text-black/70 transition hover:text-[#C6A15B]";
const ACTIVE_NAV_CLASS = "text-black data-[status=active]:text-[#C6A15B]";

export function Header() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { isAuthenticated, signOut: doSignOut } = useAuth();
  const cartCount = useCartCount();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAuthRoute = AUTH_ROUTES.has(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (isAuthRoute) return null;

  async function handleSignOut() {
    await doSignOut();
    setMobileOpen(false);
    navigate({ to: "/" });
  }

  return (
    <header
      className={
        "sticky top-0 z-40 w-full border-b bg-white/95 backdrop-blur transition " +
        (scrolled ? "border-black/10 shadow-[0_1px_0_rgba(0,0,0,0.03)]" : "border-transparent")
      }
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="mx-auto grid h-16 max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-4 px-4 sm:px-6 lg:h-20 lg:px-10">
        {/* Left: mobile menu + desktop nav (nav shown right of logo on lg) */}
        <div className="flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger
              className="inline-flex h-11 w-11 items-center justify-center text-black lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[86%] max-w-sm border-r border-black/10 bg-white p-0"
            >
              <VisuallyHidden>
                <SheetTitle>Menu</SheetTitle>
              </VisuallyHidden>
              <MobileMenu
                pathname={pathname}
                isAuthenticated={isAuthenticated}
                onNavigate={() => setMobileOpen(false)}
                onSignOut={handleSignOut}
              />
            </SheetContent>
          </Sheet>
        </div>

        {/* Center: logo */}
        <div className="flex items-center justify-center lg:justify-start lg:gap-10">
          <Link to="/" className="flex items-center">
            <img
              src={cb16Logo.url}
              alt="CB16 – The CB16 Perfume"
              data-theme-invert="true"
              className="h-12 max-h-12 w-auto object-contain md:h-[52px] md:max-h-[52px] lg:h-[60px] lg:max-h-[60px]"
            />
          </Link>
          <nav aria-label="Primary" className="hidden lg:flex lg:items-center lg:gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                activeOptions={{ exact: link.exact ?? false }}
                className={`${NAV_LINK_CLASS} ${ACTIVE_NAV_CLASS}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          <IconAction label="Search" as={Link} to="/search">
            <Search className="h-5 w-5" />
          </IconAction>
          <div className="hidden sm:block">
            <ThemeToggle />
          </div>
          {isAuthenticated ? (
            <div className="hidden items-center gap-1 sm:flex">
              <IconAction label="Account" as={Link} to="/account">
                <User className="h-5 w-5" />
              </IconAction>
              <button
                onClick={handleSignOut}
                className="hidden text-[11px] font-medium uppercase tracking-[0.22em] text-black/70 transition hover:text-[#C6A15B] lg:inline-flex"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="hidden text-[11px] font-medium uppercase tracking-[0.22em] text-black/80 transition hover:text-[#C6A15B] sm:inline-flex"
            >
              Sign In
            </Link>
          )}
          <Link
            to="/cart"
            aria-label={`Cart, ${cartCount} items`}
            className="relative inline-flex h-11 w-11 items-center justify-center text-black transition hover:text-[#C6A15B]"
          >
            <ShoppingBag className="h-5 w-5" />
            {cartCount > 0 && (
              <span className="absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#C6A15B] px-1 text-[10px] font-medium text-white">
                {cartCount}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}

function IconAction({
  label,
  children,
  as: As = "button",
  ...rest
}: {
  label: string;
  children: React.ReactNode;
  as?: React.ElementType;
  [key: string]: unknown;
}) {
  return (
    <As
      aria-label={label}
      {...rest}
      className="inline-flex h-11 w-11 items-center justify-center text-black transition hover:text-[#C6A15B]"
    >
      {children}
    </As>
  );
}

function MobileMenu({
  pathname,
  isAuthenticated,
  onNavigate,
  onSignOut,
}: {
  pathname: string;
  isAuthenticated: boolean;
  onNavigate: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="flex h-full flex-col bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="flex h-16 items-center justify-between border-b border-black/10 px-6">
        <span
          className="text-base font-medium uppercase tracking-[0.4em] text-black"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          CB16
        </span>
      </div>
      <nav aria-label="Mobile" className="flex flex-1 flex-col gap-1 px-6 py-8">
        {NAV_LINKS.map((link) => {
          const active = link.exact
            ? pathname === link.to
            : pathname.startsWith(link.to) && link.to !== "/";
          return (
            <Link
              key={link.to}
              to={link.to}
              onClick={onNavigate}
              className={
                "py-3 text-sm uppercase tracking-[0.24em] transition " +
                (active ? "text-[#C6A15B]" : "text-black hover:text-[#C6A15B]")
              }
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {link.label}
            </Link>
          );
        })}
      </nav>
      <div className="mx-6 my-4 h-px bg-[#C6A15B]/60" />
      <div className="px-6 pb-2">
        <p className="mb-1 text-[10px] uppercase tracking-[0.28em] text-black/40">Appearance</p>
        <ThemeToggle variant="rows" />
      </div>
      <div className="mx-6 my-4 h-px bg-[#C6A15B]/60" />
      <div className="flex flex-col gap-2 px-6 pb-8">
        {isAuthenticated ? (
          <>
            <Link
              to="/account"
              onClick={onNavigate}
              className="text-xs font-medium uppercase tracking-[0.24em] text-black/80 hover:text-[#C6A15B]"
            >
              My Account
            </Link>
            <button
              onClick={onSignOut}
              className="text-left text-xs font-medium uppercase tracking-[0.24em] text-black/80 hover:text-[#C6A15B]"
            >
              Sign Out
            </button>
          </>
        ) : (
          <>
            <Link
              to="/login"
              onClick={onNavigate}
              className="text-xs font-medium uppercase tracking-[0.24em] text-black/80 hover:text-[#C6A15B]"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              onClick={onNavigate}
              className="text-xs font-medium uppercase tracking-[0.24em] text-black/80 hover:text-[#C6A15B]"
            >
              Create Account
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
